/**
 * A control which displays a list of page numbers and allows for navigation between them.
 * ## Properties
 * Name | Description
 * --- | ---
 * current | Gets the currently-selected page (1-based index).
 * distance | Gets the number of additional indices, aside from the current index, to display explicitly.
 *
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * select | The new value of `current`. | Fires whenever the selected index changes.
 * @module app/views/PagerView
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
var PagerView = Backbone.View.extend({
  tagName: "ul",
  className: "pagination pager-view",

  /**
   * Initializes the control.
   * @param  {object} options The options hash.
   * @param {Integer} options.indices:4 The number of additional indices, aside from the current index, to display.
   * @param {Integer} options.count:1 The total number of selectable indices.
   * @param {Integer} options.current:1 The 1-based index to initially select.
   * @param {Integer} options.startAt The lowest index to show on initial render.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      indices: 4,
      count: 1,
      skip: true,
      current: 1,
    });

    this.current = this.options.current;
    this.distance = Math.min(this.options.count - 1, this.options.indices);
    this._start = this.current;
    this._stop = this.current + this.distance;
    this._count = this.options.count;
  },

  events: {
    "click .first": function () {
      this.setIndex(1);
    },
    "click .previous": function () {
      this.setIndex(this.getIndex() - 1);
    },
    "click .next": function () {
      this.setIndex(this.getIndex() + 1);
    },
    "click .last": function () {
      this.setIndex(this.options.count);
    },
    "click .item": function (evt) {
      this.setIndex($(evt.target).html());
    },
    keydown: function (evt) {
      var RIGHT_ARROW = 39;
      var LEFT_ARROW = 37;

      if (evt.which == RIGHT_ARROW) {
        this.setIndex(this.getIndex() + 1);
      } else if (evt.which == LEFT_ARROW) {
        this.setIndex(this.getIndex() - 1);
      }
    },
  },

  template: _.template(
    '<li class="disabled first"><a href="#"><span class="glyphicon glyphicon-fast-backward"></span></a></li><li class="disabled previous"><a href="#"><span class="glyphicon glyphicon-step-backward"></span></a></li><li class="disabled next before"><a href="#"><span class="glyphicon glyphicon-step-forward"></span></a></li><li class="disabled last"><a href="#"><span class="glyphicon glyphicon-fast-forward"></span></a></li>'
  ),

  /**
   * *Just* render, without triggering any events.
   * @private
   */
  _render: function () {
    this.$el.html(this.template(this.options));
    this.$(".item").remove();
    var before = this.$(".before");

    var start = this._startIndex();
    var end = this._stopIndex();
    var idx;

    for (idx = start; idx <= end; idx++) {
      var item = $(
        '<li class="item item-' + idx + '"><a href="#">' + idx + "</a></li>"
      );

      if (this.current == idx) {
        item.addClass("active");
      }

      before.before(item);
    }
  },

  render: function () {
    this._render();
    this.setIndex(this.current);
  },

  _updateIndices: function () {
    var start = this._startIndex();
    var current = start;

    this.$(".item").each(function () {
      $(this).find("a").html(current);
      $(this).attr("class", "item");
      $(this).addClass("item-" + current);
      current++;
    });

    this.$(".item").removeClass("active");
    this.$(".item-" + this.current).addClass("active");
    this.delegateEvents();
  },

  _startIndex: function () {
    return this._start;
  },

  _stopIndex: function () {
    return this._stop;
  },

  /**
   * Gets the current index.
   * @return {Integer} The current index.
   */
  getIndex: function () {
    return this.current;
  },

  /**
   * Determines if the given index is valid.
   * @param  {Integer} index The index to text.
   * @return {Boolean}       True if the index is valid, otherwise false.
   */
  validIndex: function (index) {
    return index > 0 && index <= this._count;
  },

  /**
   * Performs the operations which actually change the index internally.
   * @param  {Integer} index The 1-based index to select.
   * @return {Boolean}       True if the index was updated, otherwise false.
   */
  _updateIndex: function (index) {
    var result = false;

    index = Number(index);

    if (this.validIndex(index)) {
      result = true;

      if (index > this._stopIndex()) {
        this._stop = index;
        this._start = this._stop - this.distance;
      }

      if (index < this._startIndex()) {
        this._start = index;
        this._stop = this._start + this.distance;
      }

      if (index == 1) {
        this.$(".first, .previous").addClass("disabled");
      } else {
        this.$(".first, .previous").removeClass("disabled");
      }

      if (index == this._count) {
        this.$(".next, .last").addClass("disabled");
      } else {
        this.$(".next, .last").removeClass("disabled");
      }

      this.current = index; //must come before _updateIndices call, otherwise we get off-by-one with the active page indicator
      this._updateIndices();
    }

    return result;
  },

  /**
   * Sets the current index.
   * @param {Integer} index The 1-based index to select.
   */
  setIndex: function (index) {
    if (this._updateIndex(index)) {
      this.trigger("select", this.current);
    }
  },

  setCount: function (count) {
    this._count = count;
    this.distance = Math.min(count - 1, this.options.indices);
    this._stop = this.current + this.distance;
    this._render();
    this._updateIndex(this.current);
  },
});

export default PagerView;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/views/PagerView.js
// module id = 482
// module chunks = 1